/**
 * El client de l'API.
 *
 * Prim a posta: `fetch` amb el token, el refresc automàtic i la traducció dels errors
 * RFC 9457 a una excepció que la interfície pot ensenyar. No hi ha capa de memòria
 * cau aquí —això és de `sync/`, que ja té Dexie i la cua de sortida (docs/06)—, perquè
 * dues memòries cau del mateix serien dues fonts de veritat.
 *
 * **El refresc és rotatiu i només se n'intenta un a la vegada.** Amb quatre peticions
 * en paral·lel que caduquen alhora, quatre refrescos simultanis gastarien el mateix
 * token de refresc quatre vegades, i el servidor revoca la família sencera quan detecta
 * la reutilització d'un de gastat (docs/05 §1). Es comparteix una sola promesa.
 */

import { t } from '@fem-ho/contracts';

export interface Problem {
  type: string;
  title: string;
  status: number;
  /** En **anglès**, per a màquines: clients CalDAV, agents i qui programi contra l'API. */
  detail: string;
  /** Les dades de l'error. El client hi posa el text del catàleg. */
  params?: Record<string, string | number>;
}

/**
 * El text d'un error, en l'idioma de qui mira.
 *
 * El servidor envia `type` i `params`; el text el posa el catàleg. **Si no en té la
 * clau, s'ensenya el `detail` anglès**: un error nou del servidor pot ser lleig, però
 * mai deixa una pantalla muda ni obliga a desplegar les dues coses alhora.
 */
export function problemText(problem: Problem | undefined, fallback: string): string {
  if (problem == null || typeof problem.type !== 'string') return fallback;
  const slug = problem.type.slice(problem.type.lastIndexOf('/') + 1);
  const key = `error.${slug}`;
  const text = t(key, problem.params ?? {});
  return text === key ? (typeof problem.detail === 'string' ? problem.detail : fallback) : text;
}

/**
 * Què ha fallat, dit amb les paraules que ho expliquen.
 *
 * **«Alguna cosa ha fallat» era literalment el que sortia** a la pantalla d'arrencada,
 * passés el que passés: es menjava l'error i el substituïa per una frase que no distingeix
 * «aquest correu ja existeix» de «el servidor no respon». Amb una instància nova darrere
 * d'un proxy, aquesta diferència és la que et diu si has de mirar el formulari o el
 * contenidor.
 *
 * Els tres casos, i per què cadascun:
 *
 * - **El servidor no contesta** —`fetch` rebutja, o el proxy dona 502/503/504—: no és culpa
 *   del que has escrit. Dir-ho estalvia buscar una falta en un formulari que està bé.
 * - **Un error de l'API**: ja porta el text bo. `ApiError.message` és el `detail` del
 *   problema, traduït quan en tenim traducció.
 * - **Qualsevol altra cosa**: la frase de sempre.
 */
export function failureText(cause: unknown): string {
  if (cause instanceof ApiError) {
    return cause.status >= 502 && cause.status <= 504 ? t('error.unreachable') : cause.message;
  }
  // `fetch` només rebutja quan no hi ha hagut resposta: xarxa caiguda, DNS, TLS.
  if (cause instanceof TypeError) return t('error.unreachable');
  return t('error.generic');
}

/** Un error de l'API que la interfície pot ensenyar tal com ve. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | undefined;

  constructor(status: number, problem: Problem | undefined, fallback: string) {
    super(problemText(problem, fallback));
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

export interface Tokens {
  access_token: string;
  refresh_token: string;
}

const STORAGE_KEY = 'femho.tokens';

/**
 * Els tokens viuen a `localStorage` i no en una cookie.
 *
 * Una cookie de sessió faria el CSRF possible i obligaria a un token anti-CSRF a cada
 * escriptura; i com que Android fa servir la mateixa API amb `Authorization: Bearer`,
 * tenir dos mecanismes voldria dir dos camins d'autenticació al servidor, que és
 * exactament el que la regla 8 prohibeix.
 */
export function loadTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<Tokens>;
    if (typeof parsed.access_token !== 'string' || typeof parsed.refresh_token !== 'string') {
      return null;
    }
    return { access_token: parsed.access_token, refresh_token: parsed.refresh_token };
  } catch {
    // Un `localStorage` corrupte o inaccessible (navegació privada estricta) no ha de
    // deixar l'app en blanc: es tracta com si no hi hagués sessió.
    return null;
  }
}

export function saveTokens(tokens: Tokens | null): void {
  try {
    if (tokens === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // Sense emmagatzematge, la sessió dura el que duri la pestanya. És pitjor que
    // guardar-la, però molt millor que petar.
  }
}

let tokens: Tokens | null = loadTokens();
let refreshing: Promise<boolean> | null = null;
let sessionEpoch = 0;
let onSessionLost: (() => void) | null = null;

export function setTokens(next: Tokens | null): void {
  sessionEpoch += 1;
  refreshing = null;
  tokens = next;
  saveTokens(next);
}

export function currentTokens(): Tokens | null {
  return tokens;
}

/** Es crida quan el refresc falla: la interfície ha de tornar al login. */
export function onSessionExpired(handler: () => void): void {
  onSessionLost = handler;
}

async function refresh(): Promise<boolean> {
  const current = tokens;
  const epoch = sessionEpoch;
  if (current === null) return false;
  if (refreshing !== null) return refreshing;

  const pending = (async () => {
    const res = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: current.refresh_token }),
    });
    // Sortir o entrar amb un altre compte invalida les respostes que encara viatjaven.
    if (epoch !== sessionEpoch) return false;
    if (!res.ok) {
      if (res.status === 401) {
        setTokens(null);
        onSessionLost?.();
        return false;
      }
      // Un 429 o un error del servidor no revoca cap credencial.
      throw new ApiError(res.status, undefined, `HTTP ${String(res.status)}`);
    }
    const next = (await res.json()) as Tokens;
    if (epoch !== sessionEpoch) return false;
    tokens = next;
    saveTokens(next);
    return true;
  })();
  refreshing = pending;
  try {
    return await pending;
  } finally {
    if (refreshing === pending) refreshing = null;
  }
}

/** Un reintent pertany a la mateixa sessió que la petició original. */
async function withRefresh(send: () => Promise<Response>): Promise<Response> {
  const epoch = sessionEpoch;
  const access = tokens?.access_token;
  const res = await send();
  if (res.status !== 401 || tokens === null || epoch !== sessionEpoch) return res;
  // Un 401 tardà pot correspondre al token que un altre request ja ha renovat.
  if (tokens.access_token !== access || (await refresh())) {
    if (epoch === sessionEpoch) return send();
  }
  return res;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Capçaleres extra. `Authorization` i `content-type` les posa el client. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/**
 * Una petició a l'API.
 *
 * Un `401` s'intenta refrescar **un sol cop** i es reintenta. Reintentar-ho en bucle
 * amb un token que ja no val convertiria una sessió caducada en una tempesta de
 * peticions contra el servidor.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { ...options.headers };
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (tokens !== null) headers.authorization = `Bearer ${tokens.access_token}`;

    return fetch(path.startsWith('/') ? path : `/api/v1/${path}`, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  };

  const res = await withRefresh(send);

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let problem: Problem | undefined;
    try {
      problem = (await res.json()) as Problem;
    } catch {
      problem = undefined;
    }
    throw new ApiError(res.status, problem, `HTTP ${String(res.status)}`);
  }

  const text = await res.text();
  return (text === '' ? undefined : JSON.parse(text)) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal): Promise<T> =>
    request<T>(path, signal === undefined ? {} : { signal }),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', ...(body === undefined ? {} : { body }) }),
  patch: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'PATCH', body }),
  // `PUT` i no `PATCH` per als àmbits d'un agent: es desa **el conjunt sencer**, perquè
  // treure'n un és tan normal com afegir-n'hi un i amb un pedaç no se sabria dir.
  put: <T>(path: string, body: unknown): Promise<T> => request<T>(path, { method: 'PUT', body }),
  /**
   * Una resposta que **no és JSON**: el full d'instruccions de l'agent ve en Markdown i es
   * copia i es baixa tal com és. `request` intenta interpretar-ho i tornaria un objecte a
   * mig fer o petaria; això el demana com el que és.
   */
  text: async (path: string): Promise<string> => {
    const send = async (): Promise<Response> => {
      const headers: Record<string, string> = {};
      if (tokens !== null) headers.authorization = `Bearer ${tokens.access_token}`;
      return fetch(path.startsWith('/') ? path : `/api/v1/${path}`, { headers });
    };

    const res = await withRefresh(send);
    if (!res.ok) throw new ApiError(res.status, undefined, `HTTP ${String(res.status)}`);
    return res.text();
  },
  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),

  /**
   * Baixa el contingut d'un adjunt.
   *
   * **Per `fetch` i no per un `href` directe**: la sessió viu a `localStorage` i viatja a
   * `Authorization`, o sigui que una navegació del navegador cap a la ruta del contingut
   * no en porta res i rebria un 401. El fitxer s'agafa amb el token, se'n fa un `blob:` i
   * és aquell el que es clica.
   */
  download: async (path: string): Promise<Blob> => {
    const send = async (): Promise<Response> => {
      const headers: Record<string, string> = {};
      if (tokens !== null) headers.authorization = `Bearer ${tokens.access_token}`;
      return fetch(path.startsWith('/') ? path : `/api/v1/${path}`, { headers });
    };

    const res = await withRefresh(send);
    if (!res.ok) throw new ApiError(res.status, undefined, `HTTP ${String(res.status)}`);
    return res.blob();
  },

  /**
   * Puja un fitxer **tal qual**, sense `FormData`.
   *
   * El servidor espera `application/octet-stream` amb el nom a la consulta: un `File` ja
   * és un `Blob` i `fetch` el sap enviar sense copiar-lo a memòria, cosa que amb un
   * `FormData` construït a mà no passaria. Per això no va per `request()`, que serialitza
   * el cos a JSON.
   */
  upload: async <T>(path: string, file: File, options?: { asImage?: boolean }): Promise<T> => {
    const url = `${path}${path.includes('?') ? '&' : '?'}filename=${encodeURIComponent(file.name)}`;
    /**
     * **El logo va amb el seu tipus i no com a octets.** Els adjunts són qualsevol cosa i
     * el tipus el guarda la fila; el logo, en canvi, el servidor l'ha de rebutjar si no és
     * una de les tres imatges que accepta, i per fer-ho ha de saber què és.
     */
    const headers: Record<string, string> = {
      'content-type': options?.asImage === true ? file.type : 'application/octet-stream',
    };

    const send = async (): Promise<Response> => {
      if (tokens !== null) headers.authorization = `Bearer ${tokens.access_token}`;
      return fetch(url.startsWith('/') ? url : `/api/v1/${url}`, {
        method: 'POST',
        headers,
        body: file,
      });
    };

    // El mateix refresc d'una sola oportunitat que `request()`: una sessió que caduca
    // just en pujar no ha de perdre el fitxer.
    const res = await withRefresh(send);

    if (!res.ok) {
      let problem: Problem | undefined;
      try {
        problem = (await res.json()) as Problem;
      } catch {
        problem = undefined;
      }
      throw new ApiError(res.status, problem, `HTTP ${String(res.status)}`);
    }
    return (await res.json()) as T;
  },
};
